import { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import {
  Badge,
  Box,
  Button,
  IconButton,
  InputBase,
  Skeleton,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  followButtonBlue,
  followButtonBlueHover,
} from "../../constants/brandColors";
import { uiNeutrals } from "../../constants/uiNeutrals";
import { closeIconButtonSx } from "../../constants/closeIconButtonSx";
import CloseIcon from "@mui/icons-material/Close";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import logger from "../../utils/logger";
import { toSlug } from "../../utils/urlSlug";
import RestaurantItemImage from "../restaurant/RestaurantItemImage";
import { useRequireAuth } from "../../hooks/useRequireAuth";
import { useAuth } from "../../contexts/AuthContext";
import {
  followBrand,
  unfollowBrand,
  getFollowedBrands,
  getCachedFollowedBrandsBatchRows,
  setCachedFollowedBrandsBatchRows,
  isBrandFollowed,
  normalizeFollowedBrandIdsList,
} from "../../utils/userPreferences";
import { BRAND_CUISINE_FILTER_OPTIONS } from "../../constants/cuisines";
import DealListLoadMoreButton from "./dealList/components/DealListLoadMoreButton";
import {
  DEAL_LIST_BOTTOM_SCROLL_RESERVE,
  DEAL_LIST_SECTION_PAGE_SIZE,
} from "./dealList/constants";

const BRAND_LIST_PAGE_SIZE = DEAL_LIST_SECTION_PAGE_SIZE;

const CUISINE_OPTIONS = BRAND_CUISINE_FILTER_OPTIONS;

const RESTAURANT_LIST_SKELETON_ROWS = 10;

const RestaurantListSkeleton = ({ rowDivider }) => (
  <Box aria-hidden>
    {Array.from({ length: RESTAURANT_LIST_SKELETON_ROWS }).map((_, i) => (
      <Box
        key={i}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          borderBottom: `1px solid ${rowDivider}`,
          py: 1.5,
        }}
      >
        <Skeleton variant="circular" width={56} height={56} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton
            variant="text"
            width="72%"
            height={22}
            sx={{ maxWidth: 280 }}
          />
          <Skeleton
            variant="text"
            width="44%"
            height={16}
            sx={{ maxWidth: 160, mt: 0.5 }}
          />
        </Box>
        <Skeleton
          variant="rounded"
          width={88}
          height={30}
          sx={{ flexShrink: 0, borderRadius: "25px" }}
        />
      </Box>
    ))}
  </Box>
);

const formatBrandCuisineLine = (cuisine) => {
  if (cuisine == null) return null;
  if (Array.isArray(cuisine)) {
    const parts = cuisine
      .map((c) => (c != null ? String(c).trim() : ""))
      .filter(Boolean);
    return parts.length ? parts.join(" · ") : null;
  }
  const s = String(cuisine).trim();
  return s || null;
};

const BrowseRestaurantsPanel = ({
  isActive = false,
  embedInFollowingTab = false,
  /** When true (restaurant drawer), follow/unfollow edits are local until the drawer closes. */
  deferFollowPersistenceUntilClose = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const requireAuth = useRequireAuth();
  const { isAuthenticated } = useAuth();
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [brands, setBrands] = useState([]);
  const [listTotal, setListTotal] = useState(0);
  const [nextPage, setNextPage] = useState(2);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [followVersion, setFollowVersion] = useState(0);
  const [followedSectionBrands, setFollowedSectionBrands] = useState([]);
  /** Optimistic overrides for follow/unfollow UI so the button flips instantly. */
  const [optimisticFollowDelta, setOptimisticFollowDelta] = useState(
    () => new Map(),
  );
  /** Keeps skeleton up until `/brands` and followed-id batch settle so Following isn’t appended after browse rows. */
  const [followLoadPending, setFollowLoadPending] = useState(false);
  const [cuisineDialogOpen, setCuisineDialogOpen] = useState(false);
  const [selectedCuisines, setSelectedCuisines] = useState([]);
  const [cuisineDraft, setCuisineDraft] = useState([]);
  /** Followed brand ids while drawer is open with `deferFollowPersistenceUntilClose`; null = use server state. */
  const [draftFollowIdsArr, setDraftFollowIdsArr] = useState(null);
  const draftFollowIdsRef = useRef(null);
  const initialFollowIdsRef = useRef(null);
  const wasActiveForDeferFlushRef = useRef(false);
  /** While drawer + defer: frozen order/rows for Following block until drawer closes. */
  const [frozenFollowedRows, setFrozenFollowedRows] = useState([]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useLayoutEffect(() => {
    if (isActive) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [isActive]);

  useLayoutEffect(() => {
    if (isActive && isAuthenticated) {
      setFollowLoadPending(true);
    } else {
      setFollowLoadPending(false);
    }
  }, [isActive, isAuthenticated]);

  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const cuisineParam =
          selectedCuisines.length > 0 ? selectedCuisines.join(",") : undefined;
        const res = await api.get("/brands", {
          params: {
            search: debouncedSearch || undefined,
            cuisine: cuisineParam,
            page: 1,
            pageSize: BRAND_LIST_PAGE_SIZE,
          },
        });
        if (!cancelled) {
          const data = res.data?.data || [];
          const total = res.data?.pageInfo?.total ?? 0;
          setBrands(data);
          setListTotal(total);
          setNextPage(2);
        }
      } catch (e) {
        logger.error("Failed to load brands", e);
        if (!cancelled) {
          setBrands([]);
          setListTotal(0);
          setNextPage(2);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, isActive, selectedCuisines]);

  useEffect(() => {
    if (!cuisineDialogOpen) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [cuisineDialogOpen]);

  useEffect(() => {
    const onFollowedChanged = () => {
      if (deferFollowPersistenceUntilClose && isActive) return;
      setFollowVersion((v) => v + 1);
    };
    window.addEventListener("followedBrandsChanged", onFollowedChanged);
    return () =>
      window.removeEventListener("followedBrandsChanged", onFollowedChanged);
  }, [deferFollowPersistenceUntilClose, isActive]);

  useEffect(() => {
    if (!isActive || !isAuthenticated) {
      setFollowedSectionBrands([]);
      setFollowLoadPending(false);
      if (deferFollowPersistenceUntilClose) {
        setFrozenFollowedRows([]);
      }
      if (!deferFollowPersistenceUntilClose) {
        setDraftFollowIdsArr(null);
        draftFollowIdsRef.current = null;
        initialFollowIdsRef.current = null;
      }
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const ids = await getFollowedBrands();
        if (cancelled) return;
        if (!ids?.length) {
          setFollowedSectionBrands([]);
          if (deferFollowPersistenceUntilClose) {
            const empty = [];
            initialFollowIdsRef.current = empty;
            draftFollowIdsRef.current = empty;
            setDraftFollowIdsArr(empty);
            setFrozenFollowedRows([]);
          }
          return;
        }
        let rows = getCachedFollowedBrandsBatchRows(ids);
        if (!rows) {
          const res = await api.post("/brands/batch", { brandIds: ids });
          rows = Array.isArray(res.data) ? res.data : [];
          setCachedFollowedBrandsBatchRows(ids, rows);
        }
        const byId = new Map(rows.map((b) => [String(b._id), b]));
        const ordered = ids.map((id) => byId.get(String(id))).filter(Boolean);
        if (!cancelled) {
          setFollowedSectionBrands(ordered);
          if (deferFollowPersistenceUntilClose) {
            const normalized = normalizeFollowedBrandIdsList(ids).map(String);
            initialFollowIdsRef.current = [...normalized];
            draftFollowIdsRef.current = [...normalized];
            setDraftFollowIdsArr([...normalized]);
            setFrozenFollowedRows(ordered.map((b) => ({ ...b })));
          }
        }
      } catch (e) {
        logger.error("Failed to load followed restaurants", e);
        if (!cancelled) setFollowedSectionBrands([]);
      } finally {
        if (!cancelled) setFollowLoadPending(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [
    isActive,
    isAuthenticated,
    followVersion,
    deferFollowPersistenceUntilClose,
  ]);

  useEffect(() => {
    if (!deferFollowPersistenceUntilClose) {
      wasActiveForDeferFlushRef.current = isActive;
      return;
    }
    const wasActive = wasActiveForDeferFlushRef.current;
    wasActiveForDeferFlushRef.current = isActive;
    if (!wasActive || isActive) return;

    const initial = initialFollowIdsRef.current;
    const finalSnapshot = draftFollowIdsRef.current;
    initialFollowIdsRef.current = null;
    draftFollowIdsRef.current = null;
    setDraftFollowIdsArr(null);

    if (!initial || !finalSnapshot) return;

    const a = new Set(initial);
    const b = new Set(finalSnapshot);
    if (a.size === b.size && [...a].every((id) => b.has(id))) return;

    const toRemove = [...a].filter((id) => !b.has(id));
    const toAdd = [...b].filter((id) => !a.has(id));

    void (async () => {
      for (const id of toRemove) {
        await unfollowBrand(id, { quiet: true });
      }
      for (const id of toAdd) {
        await followBrand(id, { quiet: true });
      }
      if (toRemove.length > 0 || toAdd.length > 0) {
        window.dispatchEvent(new Event("followedBrandsChanged"));
      }
    })();
  }, [isActive, deferFollowPersistenceUntilClose]);

  const hasMoreBrands = brands.length < listTotal;

  const followedIdSet = useMemo(
    () => new Set(followedSectionBrands.map((b) => String(b._id))),
    [followedSectionBrands],
  );

  const effectiveFollowIdSet = useMemo(() => {
    if (deferFollowPersistenceUntilClose && draftFollowIdsArr !== null) {
      return new Set(draftFollowIdsArr);
    }
    const merged = new Set(followedIdSet);
    optimisticFollowDelta.forEach((nextIsFollowing, id) => {
      if (nextIsFollowing) merged.add(String(id));
      else merged.delete(String(id));
    });
    return merged;
  }, [
    deferFollowPersistenceUntilClose,
    draftFollowIdsArr,
    followedIdSet,
    optimisticFollowDelta,
  ]);

  const followingRowsForUi = useMemo(() => {
    if (deferFollowPersistenceUntilClose && draftFollowIdsArr !== null) {
      return frozenFollowedRows;
    }
    return followedSectionBrands;
  }, [
    deferFollowPersistenceUntilClose,
    draftFollowIdsArr,
    frozenFollowedRows,
    followedSectionBrands,
  ]);

  const isFilteringRestaurants = Boolean(
    (debouncedSearch && debouncedSearch.trim()) || selectedCuisines.length > 0,
  );

  const browseDisplayBrands = useMemo(() => {
    if (isFilteringRestaurants) return brands;
    const idsShownInFollowingSection = new Set(
      followingRowsForUi
        .map((b) => (b._id != null ? String(b._id) : null))
        .filter(Boolean),
    );
    return brands.filter(
      (b) => b._id && !idsShownInFollowingSection.has(String(b._id)),
    );
  }, [brands, followingRowsForUi, isFilteringRestaurants]);

  const handleLoadMoreBrands = async () => {
    if (!isActive || !hasMoreBrands || loadingMore || loading) return;
    setLoadingMore(true);
    try {
      const cuisineParam =
        selectedCuisines.length > 0 ? selectedCuisines.join(",") : undefined;
      const res = await api.get("/brands", {
        params: {
          search: debouncedSearch || undefined,
          cuisine: cuisineParam,
          page: nextPage,
          pageSize: BRAND_LIST_PAGE_SIZE,
        },
      });
      const data = res.data?.data || [];
      const total = res.data?.pageInfo?.total ?? listTotal;
      setListTotal(total);
      if (data.length === 0) {
        setListTotal(brands.length);
        return;
      }
      setBrands((prev) => [...prev, ...data]);
      setNextPage((p) => p + 1);
    } catch (e) {
      logger.error("Failed to load more brands", e);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleFollowToggle = async (e, brandId) => {
    e.stopPropagation();
    if (!brandId) return;
    const id = String(brandId);

    if (deferFollowPersistenceUntilClose && draftFollowIdsArr !== null) {
      const cur = new Set(draftFollowIdsArr);
      if (cur.has(id)) {
        cur.delete(id);
        const next = [...cur];
        draftFollowIdsRef.current = next;
        setDraftFollowIdsArr(next);
        return;
      }
      requireAuth(() => {
        setDraftFollowIdsArr((prev) => {
          const base = prev ? [...prev] : [];
          const s = new Set(base);
          s.add(id);
          const next = [...s];
          draftFollowIdsRef.current = next;
          return next;
        });
      });
      return;
    }

    const following = isBrandFollowed(brandId);
    if (following) {
      // Optimistic: flip UI immediately, then persist
      setOptimisticFollowDelta((prev) => {
        const next = new Map(prev);
        next.set(id, false);
        return next;
      });
      const ok = await unfollowBrand(brandId);
      if (ok) {
        window.dispatchEvent(new Event("followedBrandsChanged"));
      } else {
        // Roll back optimistic change
        setOptimisticFollowDelta((prev) => {
          const next = new Map(prev);
          next.delete(id);
          return next;
        });
      }
      return;
    }
    requireAuth(async () => {
      // Optimistic: flip UI immediately, then persist
      setOptimisticFollowDelta((prev) => {
        const next = new Map(prev);
        next.set(id, true);
        return next;
      });
      const ok = await followBrand(brandId);
      if (ok) {
        window.dispatchEvent(new Event("followedBrandsChanged"));
      } else {
        // Roll back optimistic change
        setOptimisticFollowDelta((prev) => {
          const next = new Map(prev);
          next.delete(id);
          return next;
        });
      }
    });
  };

  const n = theme.palette.neutral || uiNeutrals;

  const openCuisineDialog = () => {
    setCuisineDraft([...selectedCuisines]);
    setCuisineDialogOpen(true);
  };

  const toggleCuisineDraft = (value) => {
    setCuisineDraft((prev) =>
      prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value],
    );
  };

  const applyCuisineFilters = () => {
    setSelectedCuisines([...cuisineDraft]);
    setCuisineDialogOpen(false);
  };

  const clearCuisineDraft = () => setCuisineDraft([]);

  const showFollowingSection =
    isAuthenticated && followingRowsForUi.length > 0 && !isFilteringRestaurants;

  const showRestaurantListSkeleton = loading || followLoadPending;

  const renderRestaurantRow = (brand) => {
    const slug = toSlug(brand.name);
    const followed = brand._id
      ? deferFollowPersistenceUntilClose && draftFollowIdsArr !== null
        ? effectiveFollowIdSet.has(String(brand._id))
        : isBrandFollowed(brand._id)
      : false;
    const cuisineLine = formatBrandCuisineLine(brand.cuisine);
    return (
      <Box
        key={`${brand._id}-${followVersion}`}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          borderBottom: `1px solid ${n.rowDivider}`,
        }}
      >
        <Box
          onClick={() => {
            if (!slug) return;
            navigate(`/deals/yours/restaurant/${slug}`);
          }}
          aria-label={slug ? `View deals for ${brand.name}` : undefined}
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 2,
            minWidth: 0,
            py: 1.5,
            pr: 0.5,
            cursor: slug ? "pointer" : "default",
            borderRadius: 1,
            WebkitTapHighlightColor: "transparent",
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              overflow: "hidden",
              flexShrink: 0,
              bgcolor: "background.default",
              border: `1px solid ${n.wellBorder}`,
            }}
          >
            <RestaurantItemImage
              restaurantName={brand.name}
              imageUrl={brand.logoUrl}
              useDefaultFallback
              size="100%"
              borderRadius="50%"
              loading="lazy"
              sx={{ width: "100%", height: "100%" }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }} noWrap>
              {brand.name}
            </Typography>
            {cuisineLine ? (
              <Typography
                sx={{ fontSize: "0.75rem", color: "#888", mt: 0.25 }}
                noWrap
                title={cuisineLine}
              >
                {cuisineLine}
              </Typography>
            ) : null}
          </Box>
        </Box>

        {brand._id ? (
          <Button
            variant="contained"
            size="small"
            disableRipple
            disableElevation
            onClick={(e) => handleFollowToggle(e, brand._id)}
            onPointerDown={(e) => e.stopPropagation()}
            sx={{
              flexShrink: 0,
              alignSelf: "center",
              minWidth: 88,
              px: 1.5,
              py: 1,
              fontSize: "0.8125rem",
              fontWeight: 600,
              letterSpacing: "0.01em",
              textTransform: "none",
              borderRadius: "25px",
              boxShadow: "none",
              lineHeight: 1.2,
              minHeight: 30,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              ...(followed
                ? {
                    bgcolor: n.followingControl,
                    color: "#000",
                    "&:hover": { bgcolor: n.followingControlHover },
                  }
                : {
                    bgcolor: followButtonBlue,
                    color: "#fff",
                    "&:hover": {
                      bgcolor: followButtonBlueHover,
                      borderColor: "rgba(0, 0, 0, 0.08)",
                    },
                  }),
            }}
          >
            {followed ? "Following" : "Follow"}
          </Button>
        ) : null}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        maxWidth: 720,
        mx: "auto",
        px: { xs: 2 },
        pt: { xs: 0.5, md: 0 },
        pb: embedInFollowingTab ? DEAL_LIST_BOTTOM_SCROLL_RESERVE : 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          py: 0.85,
          px: 1.35,
          mb: 1,
          borderRadius: 2,
          bgcolor: n.surface,
          border: "none",
          boxShadow: "none",
        }}
      >
        <SearchIcon sx={{ color: "#888", fontSize: 20, flexShrink: 0 }} />
        <InputBase
          placeholder="Search restaurants"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          autoFocus={false}
          sx={{
            flex: 1,
            minWidth: 0,
            "& .MuiInputBase-input": {
              fontSize: "16px",
              lineHeight: 1.25,
            },
          }}
        />
        <Badge
          color="default"
          overlap="circular"
          badgeContent={selectedCuisines.length || null}
          invisible={selectedCuisines.length === 0}
          sx={{
            flexShrink: 0,
            "& .MuiBadge-badge": {
              fontSize: "0.65rem",
              minWidth: 16,
              height: 16,
              bgcolor: "#000",
              color: "#fff",
            },
          }}
        >
          <IconButton
            type="button"
            size="small"
            onClick={openCuisineDialog}
            aria-label="Filter by cuisine"
            sx={{
              p: 0.35,
              color: "#888",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            <RestaurantIcon sx={{ fontSize: 22 }} />
          </IconButton>
        </Badge>
      </Box>

      {cuisineDialogOpen && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1300,
            bgcolor: "rgba(255, 255, 255, 0.5)",
            backdropFilter: "blur(2px)",
            WebkitBackdropFilter: "blur(2px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            px: 2,
          }}
          onClick={() => setCuisineDialogOpen(false)}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              width: "100%",
              maxWidth: 340,
              maxHeight: "65vh",
              bgcolor: "background.default",
              borderRadius: "16px",
              border: "1px solid #ddd",
              boxShadow: "none",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: 2,
                pt: 2,
                pb: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography sx={{ fontSize: "16px", fontWeight: 600, flex: 1 }}>
                Type
              </Typography>
              <IconButton
                size="small"
                onClick={() => setCuisineDialogOpen(false)}
                aria-label="Close"
                sx={closeIconButtonSx}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                px: 2,
                py: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                {CUISINE_OPTIONS.map((opt) => {
                  const isOn = cuisineDraft.includes(opt.value);
                  return (
                    <Box
                      key={opt.value}
                      component="button"
                      type="button"
                      onClick={() => toggleCuisineDraft(opt.value)}
                      sx={{
                        cursor: "pointer",
                        background: "#fff",
                        color: "#000",
                        borderRadius: "20px",
                        px: 2,
                        py: 1,
                        minHeight: "36px",
                        display: "inline-flex",
                        alignItems: "center",
                        fontSize: "14px",
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        border: isOn ? "1px solid #000" : "1px solid #ddd",
                        transition: "all 0.2s ease",
                        "&:hover": { bgcolor: uiNeutrals.surface },
                      }}
                    >
                      {opt.label}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Box
              sx={{
                flexShrink: 0,
                px: 2,
                py: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              <Button
                size="small"
                onClick={clearCuisineDraft}
                sx={{ textTransform: "none", color: "#666" }}
              >
                Clear all
              </Button>
              <Button
                variant="contained"
                disableElevation
                onClick={applyCuisineFilters}
                sx={{
                  textTransform: "none",
                  borderRadius: "20px",
                  px: 2.5,
                  bgcolor: "#000",
                  color: "#fff",
                  "&:hover": { bgcolor: "#333" },
                }}
              >
                Done
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {showRestaurantListSkeleton ? (
        <RestaurantListSkeleton rowDivider={n.rowDivider} />
      ) : (
        <>
          {showFollowingSection ? (
            <Box sx={{ mb: 0.5 }}>
              <Box>
                {followingRowsForUi.map((brand) => renderRestaurantRow(brand))}
              </Box>
            </Box>
          ) : null}

          {brands.length === 0 && !showFollowingSection ? (
            <Typography sx={{ color: "#666", textAlign: "center", py: 6 }}>
              No restaurants found.
            </Typography>
          ) : (
            <Box>
              {browseDisplayBrands.map((brand) => renderRestaurantRow(brand))}
              {hasMoreBrands ? (
                <DealListLoadMoreButton
                  onClick={handleLoadMoreBrands}
                  loading={loadingMore}
                  disabled={loading}
                />
              ) : null}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default BrowseRestaurantsPanel;
